import { useCallback, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

// Walking a set of pictures with ← and → while one of them is open, and the two arrows
// that do the same with the mouse. Shared by the zooms (ImageLightbox, CatalogImageModal)
// so a gallery steps the same way wherever it is opened from — the only difference is what
// a picture is identified by: a URL in one, a database id in the other.

// The two step arrows, vertically centred on the edges of the zoom's CARD — in its side
// gutters, which the card widens for them — and styled like the card's other buttons.
// `cursor-default` undoes the backdrop's zoom-out cursor: these do not close, and a
// control that looks like the thing that closes is a control you daren't press.
const arrowCls =
  'absolute top-1/2 z-10 -translate-y-1/2 cursor-default rounded-full border border-slate-300 bg-white p-2 text-slate-700 shadow-sm transition-colors hover:border-blue-500 hover:bg-blue-50';

// The keys, claimed OUTRIGHT while a steppable zoom is open: registered in the capture
// phase and stopped dead there, exactly as the skimmers do it, because PdfViewer pages the
// manual on the same two keys from its own window listener. Left to bubble, one press would
// step the gallery AND turn the page behind it.
//
// Only when there is somewhere to go: a lone picture leaves the keys to the reader
// underneath, which is whose they are the rest of the time.
// HOW MANY OVERLAYS ARE HOLDING THE ARROW KEYS right now. A page-level stepper listening on the
// same keys — StepPromptNav, walking a badge's steps — asks this and stands aside: an open
// picture is what the arrows are about while it is open, and both listen in the capture phase
// on window, where the one bound FIRST wins, which was always the page's.
let overlaysStepping = 0;
export const overlayHoldsArrows = () => overlaysStepping > 0;

export function useStepThrough<T>(
  // The set the open picture belongs to, in the order it is shown. Undefined or single
  // for a lone picture: nothing to step through, and `canStep` is false.
  list: T[] | undefined,
  // Which of them is open (compared by ===, so ids or URLs, not objects).
  current: T | null | undefined,
  onNavigate: ((item: T) => void) | undefined,
) {
  const items = list ?? [];
  const at = current == null ? -1 : items.indexOf(current);
  const total = items.length;
  const canStep = at >= 0 && total > 1 && !!onNavigate;

  // Wraps at both ends. A gallery of photos has no meaningful first or last, and an arrow
  // that is always live is one less dead control to explain.
  //
  // Through a ref so `step` — and the key listener below — stay put between renders: the
  // list and the callback are rebuilt by the caller on every one, and subscribing to the
  // window again each time is churn the listener does not need.
  const stepRef = useRef<(by: number) => void>(() => {});
  stepRef.current = (by: number) => {
    if (!canStep) return;
    onNavigate!(items[(at + by + total) % total]);
  };
  const step = useCallback((by: number) => stepRef.current(by), []);

  useEffect(() => {
    if (!canStep) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      stepRef.current(e.key === 'ArrowLeft' ? -1 : 1);
    };
    window.addEventListener('keydown', onKey, true);
    overlaysStepping += 1;
    return () => {
      window.removeEventListener('keydown', onKey, true);
      overlaysStepping -= 1;
    };
  }, [canStep]);

  return { at, total, canStep, step };
}

// THE ARROW KEYS ON A PAGE, rather than in an overlay — for the steppers that sit in the
// middle of a phase and step through its own list: sub-steps, parts, steps.
//
// THREE THINGS SEPARATE IT FROM useStepThrough above, and all three follow from the page not
// being modal:
//
// It listens in the BUBBLE phase. An overlay's own stepper listens in capture and stops the
// event dead, so opening a lightbox over the page takes the arrows with it and gives them back
// on close — without either control knowing the other exists.
//
// IT LEAVES A TYPIST ALONE. Half of these phases have a field you can edit; in one, the part's
// name is a row of the table. An arrow key in a text box moves the caret, and a page that
// stepped away from the field mid-word would be unusable.
//
// AND IT ENDS RATHER THAN WRAPS. The lists are the manual's own order — sub-step 1A to 4C,
// part 1 to 14 — so they have a first and a last, and wrapping from one to the other would say
// the list is a ring. Which is also what the arrows under them draw: disabled at the ends.
//
// IT ONLY ANSWERS WHILE IT IS ON SCREEN, and that is not a nicety: this app keeps every phase
// MOUNTED and hides the ones you are not on (see NoticeViewer, which needs them alive so a run
// started on one goes on running while you read another). So four of these listen at once, and
// the first to bind would otherwise take the arrows for the whole document — which is exactly
// what happened: the parts strip stepped on every phase and no sub-step navigator ever moved.
//
// Hence the ref, which the caller puts on the control's own box. A box inside a hidden phase
// has no client rects, so it declines the press and — crucially — does not stop it, leaving it
// for whichever stepper IS on screen.
export function useArrowSteps<T extends HTMLElement>(
  at: number, count: number, onGo: (index: number) => void, on = true,
) {
  const box = useRef<T | null>(null);
  const goRef = useRef<(by: number) => void>(() => {});
  goRef.current = (by: number) => {
    const next = at + by;
    if (next >= 0 && next < count) onGo(next);
  };
  const live = on && count > 1;
  useEffect(() => {
    if (!live) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (el?.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      // Drawn, or not this stepper's press. `getClientRects` is empty for anything a
      // `display: none` ancestor is hiding, which is how a phase is put away here.
      const own = box.current;
      if (!own || !own.isConnected || own.getClientRects().length === 0) return;
      e.preventDefault();
      // So that two page steppers on one phase cannot both move: whichever bound first wins,
      // and the phases that stack a step selector over a sub-step one give the arrows to the
      // finer of the two on purpose. See PublishStep and MergedStep.
      e.stopImmediatePropagation();
      goRef.current(e.key === 'ArrowLeft' ? -1 : 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [live]);
  return box;
}

// The previous/next arrows, flanking the picture rather than touching it: the image is the
// thing being inspected, and a control sitting on top of it hides the pixels it was opened
// for. They stop the click from reaching the backdrop, which closes. Position themselves
// against the nearest positioned ancestor — the overlay — so they belong in it, beside the
// picture's box and not inside it.
export function StepArrows({ onStep }: { onStep: (by: number) => void }) {
  const { t } = useLanguage();
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onStep(-1);
        }}
        aria-label={t('notice.gallery.prev')}
        title={t('notice.gallery.prev')}
        className={`${arrowCls} left-3`}
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onStep(1);
        }}
        aria-label={t('notice.gallery.next')}
        title={t('notice.gallery.next')}
        className={`${arrowCls} right-3`}
      >
        <ChevronRight className="h-6 w-6" />
      </button>
    </>
  );
}
